import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import { debounceTime, startWith, tap } from 'rxjs/operators';
import { BuyProduct } from '@prlw/core/buy-product/buy-product.entity';
import { BuyProductStatus } from '@prlw/core/buy-product/buy-product-status.enum';
import {
  Destination,
  DestinationEntity,
} from '@prlw/core/destinations/destination.entity';
import { Instrument } from '@prlw/core/monitor/instrument.entity';
import { ResponsiveService } from '@prlw/libs/responsive/responsive.service';

const DEBOUNCE_TIME = 500;

@Component({
  selector: 'prlw-buy-product-ui',
  templateUrl: './buy-product.component.html',
  styleUrls: ['./buy-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyProductComponent implements OnInit {
  public station = new UntypedFormControl(null, [Validators.required]);
  public volume = new FormControl();
  public isStationSelected = false;

  @Input() public instrument: Instrument | null = null;
  @Input() public status: BuyProductStatus | null = BuyProductStatus.INIT;
  @Input() public destinations: DestinationEntity[] | null = [];
  @Input() public currentDestination: Destination | null = null;

  @Output()
  public readonly closeOverlay = new EventEmitter<void>();
  @Output()
  public readonly stationChange = this.station.valueChanges.pipe(
    startWith(''),
    tap(() => {
      this.isStationSelected = false;
    }),
    debounceTime(DEBOUNCE_TIME),
  ) as EventEmitter<string>;
  @Output()
  public readonly buyRequest = new EventEmitter<BuyProduct>();
  @Output()
  public readonly repeatBuyRequest = new EventEmitter();
  @Output()
  public readonly setCurrentDestination = new EventEmitter<Destination>();
  @Output()
  public readonly eventOpenDestinationsOverlay = new EventEmitter<void>();

  constructor(private readonly responsiveService: ResponsiveService) {}

  xsmall$ = this.responsiveService.xsmall$;

  ngOnInit(): void {
    if (this.currentDestination) {
      this.setCurrent(this.currentDestination);
    }
  }

  public close(): void {
    this.closeOverlay.emit();
  }

  public get disabledBtn(): boolean {
    return (
      this.station.invalid || this.volume.invalid || !this.isStationSelected
    );
  }

  public makeBuyRequest(): void {
    if (this.instrument) {
      const product: BuyProduct = {
        instrumentName: this.instrument.instrumentName,
        lastDealPrice: this.instrument.quote.lastDealPrice,
        shipmentCode: this.instrument.shipment.code,
        shipmentName: this.instrument.shipment.name,
        stations: this.station.value,
        tariff: this.instrument.tariff ?? 0,
        totalPrice: this.instrument.totalPrice,
        volume: this.volume.value ?? 0,
      };
      this.buyRequest.emit(product);
    }
  }

  public repeat(): void {
    this.repeatBuyRequest.emit();
  }

  public setCurrent(destination: Destination): void {
    this.station.setValue(destination.name);
    this.isStationSelected = true;
    this.setCurrentDestination.emit(destination);
  }

  public isInit(status: BuyProductStatus | null): boolean {
    return status === BuyProductStatus.INIT;
  }

  public isSending(status: BuyProductStatus | null): boolean {
    return status === BuyProductStatus.SENDING;
  }

  public isSent(status: BuyProductStatus | null): boolean {
    return status === BuyProductStatus.SENT;
  }

  public isError(status: BuyProductStatus | null): boolean {
    return status === BuyProductStatus.ERROR;
  }

  valueIsNotAsterisks(value: number | '*****'): value is number {
    return value !== '*****';
  }

  public get calculateTotalPrice() {
    if (!this.instrument) return null;
    const { totalPrice } = this.instrument;
    if (!this.valueIsNotAsterisks(totalPrice)) return totalPrice;
    return this.volume.value * totalPrice;
  }

  public get totalPriceIsNotAsterisks() {
    if (!this.instrument) return false;
    return this.valueIsNotAsterisks(this.instrument.totalPrice);
  }

  public processPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  public get minAmount() {
    if (!this.instrument) return null;
    return Number(this.instrument.instrumentCode.slice(7, -1));
  }

  public setVolume(volume: FormControl) {
    this.volume = volume;
  }
}
